// ============================================================================
// Audit trail viewer — the chain of custody for one envelope.
//
// This is the evidence you would put in front of somebody disputing a
// signature, so it is written to be read by a person rather than a developer:
// plain-English event names, the actual IP and browser, and consent and
// signature shown as separate moments with their own timestamps. Raw jsonb
// detail is available but folded away.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  CheckCircle2, ChevronDown, Eye, FileCheck2, Loader2, Mail, PenLine,
  ShieldCheck, XCircle,
} from 'lucide-react';
import { EsignEvent, listEnvelopeEvents } from './envelope-lib';

/** Plain-English label + icon per audit action. */
const ACTION_META: Record<string, { label: string; icon: typeof Mail }> = {
  sent: { label: 'Signing link emailed', icon: Mail },
  identity_verified: { label: 'Identity verified', icon: ShieldCheck },
  viewed: { label: 'Document opened', icon: Eye },
  consented: { label: 'Agreed to sign electronically', icon: FileCheck2 },
  signed: { label: 'Signed', icon: PenLine },
  completed: { label: 'Completed by all parties', icon: CheckCircle2 },
  declined: { label: 'Declined', icon: XCircle },
  voided: { label: 'Voided', icon: XCircle },
};

function fmt(ts: string): string {
  const d = new Date(ts);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })}`;
}

/** Short, honest description of how identity was established. */
function verificationSummary(e: EsignEvent): string | null {
  if (e.action !== 'identity_verified') return null;
  const method = String((e.detail ?? {}).method ?? '');
  if (method === 'emailed_link') {
    return 'Opened a link we emailed to their address on file (no one-time code was required)';
  }
  if (method === 'email_otp') return 'Entered a one-time code sent by email';
  return method || null;
}

function detailLine(e: EsignEvent): string | null {
  const d = e.detail ?? {};
  if (e.action === 'consented') {
    const v = d.disclosure_version ? ` · disclosure ${d.disclosure_version}` : '';
    const gate = d.gate === 'pre_document'
      ? 'before the document was opened'
      : 'at submission';
    return `Agreed ${gate}${v}`;
  }
  if (e.action === 'signed') {
    const how = d.adoption_method ? `Signature ${d.adoption_method}` : null;
    const hash = d.document_hash
      ? `document fingerprint ${String(d.document_hash).slice(0, 12)}…`
      : null;
    return [how, hash].filter(Boolean).join(' · ') || null;
  }
  if (e.action === 'sent') {
    const del = d.delivery as { delivered?: boolean; reason?: string } | undefined;
    if (del?.delivered === true) return 'Delivered';
    if (del?.delivered === false) {
      return `Not delivered${del.reason ? ` (${del.reason})` : ''} — link shared manually`;
    }
  }
  return null;
}

export function AuditTrail({ envelopeId }: { envelopeId: string }) {
  const [events, setEvents] = useState<EsignEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [rawFor, setRawFor] = useState<string | null>(null);

  useEffect(() => {
    if (!open || events) return;
    listEnvelopeEvents(envelopeId)
      .then(setEvents)
      .catch((e: Error) => setError(e.message));
  }, [open, events, envelopeId]);

  return (
    <section className="rounded-md border border-tcs-border bg-tcs-raised">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-3 text-left"
      >
        <FileCheck2 className="h-4 w-4 shrink-0 text-tcs-accent-ink" aria-hidden />
        <span className="flex-1 text-sm font-semibold text-tcs-text">
          Audit trail
        </span>
        {events && (
          <span className="text-xs text-tcs-text-muted">{events.length} events</span>
        )}
        <ChevronDown
          className={`h-4 w-4 text-tcs-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="border-t border-tcs-border p-3">
          {error && <p role="alert" className="text-sm text-st-danger-fg">{error}</p>}
          {!events && !error && (
            <p className="flex items-center gap-2 text-sm text-tcs-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
            </p>
          )}
          {events?.length === 0 && (
            <p className="text-sm text-tcs-text-muted">
              Nothing recorded yet — the trail starts when the envelope is sent.
            </p>
          )}

          {events && events.length > 0 && (
            <ol className="space-y-3">
              {events.map((e) => {
                const meta = ACTION_META[e.action] ?? { label: e.action, icon: FileCheck2 };
                const Icon = meta.icon;
                const extra = detailLine(e);
                const verify = verificationSummary(e);
                return (
                  <li key={e.event_id} className="flex gap-2.5">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-tcs-bg">
                      <Icon className="h-3.5 w-3.5 text-tcs-accent-ink" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-tcs-text">
                        {meta.label}
                        {e.signer_name || e.signer_email
                          ? ` — ${e.signer_name ?? e.signer_email}`
                          : ''}
                      </p>
                      <p className="text-xs text-tcs-text-muted">{fmt(e.occurred_at)}</p>
                      {verify && (
                        <p className="mt-0.5 text-xs text-tcs-text-secondary">{verify}</p>
                      )}
                      {extra && (
                        <p className="mt-0.5 text-xs text-tcs-text-secondary">{extra}</p>
                      )}
                      {(e.ip_address || e.user_agent) && (
                        <p className="mt-0.5 text-[11px] text-tcs-text-muted">
                          {e.ip_address ?? 'IP not recorded'}
                          {e.user_agent ? ` · ${e.user_agent.slice(0, 60)}` : ''}
                        </p>
                      )}
                      {e.detail && Object.keys(e.detail).length > 0 && (
                        <>
                          <button
                            type="button"
                            onClick={() => setRawFor(rawFor === e.event_id ? null : e.event_id)}
                            className="mt-0.5 text-[11px] text-tcs-accent-ink underline"
                          >
                            {rawFor === e.event_id ? 'Hide raw record' : 'Raw record'}
                          </button>
                          {rawFor === e.event_id && (
                            <pre className="mt-1 overflow-x-auto rounded bg-tcs-bg p-2 text-[10px] text-tcs-text-secondary">
                              {JSON.stringify(e.detail, null, 2)}
                            </pre>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          <p className="mt-3 border-t border-tcs-border pt-2 text-[11px] text-tcs-text-muted">
            These events are also printed on the Certificate of Completion
            attached to the signed document.
          </p>
        </div>
      )}
    </section>
  );
}
